/**
 * Currency store — the single source of truth for the user's chosen DISPLAY
 * currency across every money view in the SPA (plans, overage, credit packs,
 * ads budgets…).
 *
 * Same zustand pattern as `i18n/store.ts` and `stores/ui-store.ts`:
 *   • persisted to localStorage under `karexia.currency` (raw lower-case code),
 *   • default **CAD** (the platform's billing default),
 *   • `supported` is seeded from a small fallback list and refreshed from the
 *     API (`/api/v1/plans`, `/api/v1/credits/packs` return `supported_currencies`
 *     + `default_currency`), so the selector always offers exactly what the
 *     backend actually prices.
 *
 * Consumed through the `useCurrency()` hook below, which also binds a
 * `format(amount, currencyOverride?)` helper to the active currency.
 */
import { create } from 'zustand';

import {
  DEFAULT_CURRENCY,
  FALLBACK_CURRENCIES,
  formatMoney,
  normalizeCurrency,
  type FormatMoneyOptions,
} from '@/lib/money';

export const CURRENCY_STORAGE_KEY = 'karexia.currency';

/** Read the persisted currency, coercing any legacy/absent value to CAD. */
function readInitialCurrency(): string {
  try {
    const raw = localStorage.getItem(CURRENCY_STORAGE_KEY);
    if (raw && raw.trim()) return normalizeCurrency(raw);
  } catch {
    /* private mode / disabled storage → fall through to the default */
  }
  return DEFAULT_CURRENCY;
}

/** Persist the raw currency code; ignore quota / private-mode failures. */
function persistCurrency(code: string): void {
  try {
    localStorage.setItem(CURRENCY_STORAGE_KEY, code);
  } catch {
    /* ignore */
  }
}

/** De-duplicate + normalise a currency list, keeping order, non-empty only. */
function normalizeList(list: readonly string[] | null | undefined): string[] {
  const out: string[] = [];
  for (const c of list ?? []) {
    const code = normalizeCurrency(c);
    if (code && !out.includes(code)) out.push(code);
  }
  return out.length > 0 ? out : [...FALLBACK_CURRENCIES];
}

interface CurrencyState {
  /** Active display currency (lower-case ISO code). */
  currency: string;
  /** Currencies the backend actually prices (seeded, then API-refreshed). */
  supported: string[];
  /** Switch the display currency: validates, persists, re-renders subscribers. */
  setCurrency: (code: string) => void;
  /**
   * Merge a `supported_currencies` set reported by the API. If the active
   * currency isn't in the (non-empty) supported set, fall back to CAD so we
   * never keep a currency the backend can't price.
   */
  setSupported: (list: readonly string[] | null | undefined) => void;
}

export const useCurrencyStore = create<CurrencyState>((set, get) => ({
  currency: readInitialCurrency(),
  supported: [...FALLBACK_CURRENCIES],
  setCurrency: (code) => {
    const next = normalizeCurrency(code);
    if (next === get().currency) return;
    persistCurrency(next);
    set({ currency: next });
  },
  setSupported: (list) => {
    const supported = normalizeList(list);
    const current = get().currency;
    // Keep the user's choice when still priced; otherwise fall back to CAD.
    const currency = supported.includes(current) ? current : DEFAULT_CURRENCY;
    if (currency !== current) persistCurrency(currency);
    set({ supported, currency });
  },
}));

export interface UseCurrency {
  currency: string;
  supported: string[];
  setCurrency: (code: string) => void;
  setSupported: (list: readonly string[] | null | undefined) => void;
  /** Format an amount in the active currency (or an explicit override). */
  format: (amount: number | null | undefined, override?: string, opts?: FormatMoneyOptions) => string;
}

/**
 * The app-wide currency hook. Every money view calls this so a change in the
 * selector instantly re-renders all amounts.
 */
export function useCurrency(): UseCurrency {
  const currency = useCurrencyStore((s) => s.currency);
  const supported = useCurrencyStore((s) => s.supported);
  const setCurrency = useCurrencyStore((s) => s.setCurrency);
  const setSupported = useCurrencyStore((s) => s.setSupported);
  return {
    currency,
    supported,
    setCurrency,
    setSupported,
    format: (amount, override, opts) => formatMoney(amount, override ?? currency, opts),
  };
}
